import { useLocale } from "next-intl";
import { useQuery } from "@tanstack/react-query";

import { SportEventsNewQueryKeys } from "./sportEventsNewQueryKeys";
import { getSportEventsNew, SportType } from "@/api/sportEventsNew";

import { FeedType } from "@/types/SportsApiResponses";
import { getRefetchIntervalByFeedType } from "@/utils/sportEvents";

 
export interface QueryOptions {
  feedType: FeedType;
  sportType: SportType;
  page?: number;
  pageSize?: number;
  minOffset?: string | number;
  leagueIds?: number[];
  popularLeaguesOnly?: boolean;
  excludeMarkets?: boolean;
  teamName?: string;
  // Opt-in "N odds" badge count; part of `rest`, so it is in the query key.
  withOddsCount?: boolean;
}

interface Options {
  enabled?: boolean;
  queryOptions: QueryOptions;
  /**
   * Restrict which query-state changes re-render the subscriber (TanStack's
   * notifyOnChangeProps). A polling live board passes ["data"] so the 2×
   * isFetching flips of every poll cycle stop re-rendering the whole tree
   * when the payload did not change. Omitted = notify on everything, as
   * before.
   */
  notifyOnChangeProps?: ("data" | "isLoading" | "isPending" | "error")[];
}

export const useSportEventsNewList = (options: Options) => {
  const locale = useLocale();

  const { feedType, sportType, ...rest } = options.queryOptions;

  return useQuery({
    queryKey: [SportEventsNewQueryKeys.List, locale, feedType, sportType, rest],
    enabled: options.enabled,
    notifyOnChangeProps: options.notifyOnChangeProps,
    refetchInterval: getRefetchIntervalByFeedType(feedType),
    // The bulletin must never be served stale: an admin closing a league /
    // country expects it gone from every open board immediately. The socket
    // sportEventsChanged invalidation covers connected tabs; staleTime 0 makes
    // the window-focus refetch cover the rest (the global 60s default would
    // skip it), so returning to a board always shows the current bulletin.
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const params = { lang: locale, ...options.queryOptions };
      return await getSportEventsNew({ signal, params }); 
    },
  });
};
